import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Send } from "lucide-react";
import { useCreateForumThreadMutation } from "../api/forumApi";
import { AttachmentPicker } from "../components/Attachments";

export default function NewThreadPage() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [create, { isLoading }] = useCreateForumThreadMutation();

  const submit = async () => {
    setError(null);
    if (!title.trim() || !body.trim()) {
      setError("A discussion needs a subject and something to discuss.");
      return;
    }

    try {
      const thread = await create({ title: title.trim(), body: body.trim(), files }).unwrap();
      navigate(`/forum/${thread.id}`);
    } catch (e: any) {
      setError(e?.data?.message || e?.error || "The discussion could not be posted.");
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <button
        type="button"
        onClick={() => navigate("/forum")}
        className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.15em] text-gray-400 hover:text-gray-600 mb-5"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to the forum
      </button>

      <h1 className="text-xl font-black text-gray-900 tracking-tight">New discussion</h1>
      <p className="text-xs text-gray-500 mt-2 mb-6">
        Visible to the National Policy Committee only.
      </p>

      <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm space-y-5">
        <div>
          <label className="block text-[10px] font-black uppercase tracking-[0.15em] text-gray-500 mb-2">
            Subject
          </label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={255}
            disabled={isLoading}
            placeholder="What is this about?"
            className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-400"
          />
        </div>

        <div>
          <label className="block text-[10px] font-black uppercase tracking-[0.15em] text-gray-500 mb-2">
            Your post
          </label>
          {/* Plain text on purpose. A rich-text body would have to be stored
              as HTML and rendered back with dangerouslySetInnerHTML, which
              turns every member of the committee into someone who can run
              script in every other member's browser. Line breaks are kept. */}
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={isLoading}
            rows={12}
            placeholder="Set out the matter…"
            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm leading-relaxed focus:outline-none focus:border-gray-400"
          />
        </div>

        <AttachmentPicker files={files} onChange={setFiles} disabled={isLoading} />

        {error && (
          <div className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3">
            <p className="text-xs font-bold text-rose-700">{error}</p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={() => navigate("/forum")}
            disabled={isLoading}
            className="rounded-xl border border-gray-200 px-4 py-2.5 text-[10px] font-black uppercase tracking-[0.15em] text-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded-xl bg-gray-900 px-5 py-2.5 text-[10px] font-black uppercase tracking-[0.15em] text-white hover:bg-gray-800 disabled:opacity-60"
          >
            {isLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
            Post discussion
          </button>
        </div>
      </div>
    </div>
  );
}
